import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const KEY = 'rj-dock';
interface Saved { x: number | null; y: number | null; vertical: boolean; collapsed?: boolean }

function load(): Saved {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || '');
    if (s && typeof s.vertical === 'boolean') return s;
  } catch {
    /* first visit, or storage blocked */
  }
  return { x: null, y: null, vertical: false };
}

/** True on phones and small tablets (the same width the CSS uses). */
export function useNarrow(): boolean {
  const q = '(max-width: 899px)';
  const [n, setN] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const f = () => setN(m.matches);
    f();
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return n;
}

/**
 * The tool links of the reader as a small panel at the bottom of the page on a phone.
 * Drag the grip to put it anywhere; the turn button switches between a row and a column; the arrow folds it away (so it never covers the paper); double-tap the grip to put it back.
 * Pop-up menus open towards the side that has room.
 */
export function Dock({ children }: { children: React.ReactNode }) {
  const [s, setS] = useState<Saved>(load);
  const [, tick] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  const save = (n: Saved) => {
    setS(n);
    try {
      localStorage.setItem(KEY, JSON.stringify(n));
    } catch {
      /* not remembered */
    }
  };

  const clamp = useCallback((x: number, y: number) => {
    const r = box.current?.getBoundingClientRect();
    const w = r?.width ?? 0, h = r?.height ?? 0;
    return { x: Math.min(Math.max(4, x), Math.max(4, window.innerWidth - w - 4)), y: Math.min(Math.max(4, y), Math.max(4, window.innerHeight - h - 4)) };
  }, []);

  // keep it on screen after turning, resizing or rotating the phone; publish where it is so menus know where to open
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const place = () => {
      if (s.x !== null && s.y !== null) {
        const c = clamp(s.x, s.y);
        el.style.left = c.x + 'px';
        el.style.top = c.y + 'px';
      }
      const r = el.getBoundingClientRect();
      // the second flash-news bar sits above the panel while the panel is at its home place at the bottom
      // (measured from where the panel really is, so it also works after the panel was dragged: a wide panel in the lower half pushes the bar up)
      const lower = r.top + r.height / 2 > window.innerHeight / 2 && r.width > window.innerWidth * 0.6;
      document.documentElement.style.setProperty('--dock-h', (lower ? Math.max(0, window.innerHeight - r.top) + 8 : 0) + 'px');
      el.style.setProperty('--dk-top', r.top + 'px');
      el.style.setProperty('--dk-bottom', r.bottom + 'px');
      el.style.setProperty('--dk-left', r.left + 'px');
      el.style.setProperty('--dk-right', r.right + 'px');
      const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
      el.dataset.v = cy > window.innerHeight / 2 ? 'up' : 'down';
      el.dataset.h = cx > window.innerWidth / 2 ? 'left' : 'right';
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(el);
    window.addEventListener('resize', place);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', place);
      document.documentElement.style.removeProperty('--dock-h');
    };
  });

  // spread the buttons evenly: as many columns as give equal rows (at most 4 across, so every label fits)
  useLayoutEffect(() => {
    const body = box.current?.querySelector('.dock-body') as HTMLElement | null;
    if (!body) return;
    const count = (n: Element): number => {
      let c = 0;
      for (const ch of Array.from(n.children)) {
        const cs = getComputedStyle(ch);
        if (cs.display === 'contents') c += count(ch);
        else if (cs.display !== 'none' && cs.position !== 'fixed' && cs.position !== 'absolute') c++;
      }
      return c;
    };
    const n = count(body);
    const rows = Math.max(1, Math.ceil(n / 4));
    body.style.setProperty('--cols', String(Math.max(1, Math.ceil(n / rows))));
  });

  const down = (e: React.PointerEvent) => {
    const el = box.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = box.current;
    if (!d || !el) return;
    const c = clamp(e.clientX - d.dx, e.clientY - d.dy);
    el.style.left = c.x + 'px';
    el.style.top = c.y + 'px';
    el.style.bottom = 'auto';
    el.style.right = 'auto';
    el.classList.add('moved');
    tick((n) => n + 1);
  };
  const up = () => {
    const el = box.current;
    if (drag.current && el) {
      const r = el.getBoundingClientRect();
      save({ ...s, x: r.left, y: r.top });
    }
    drag.current = null;
  };
  const reset = () => {
    const el = box.current;
    if (el) {
      el.style.left = '';
      el.style.top = '';
      el.style.bottom = '';
      el.style.right = '';
      el.classList.remove('moved');
    }
    save({ x: null, y: null, vertical: s.vertical, collapsed: s.collapsed });
  };
  const turn = () => save({ ...s, vertical: !s.vertical });
  const fold = () => save({ ...s, collapsed: !s.collapsed });

  const placed = s.x !== null && s.y !== null;
  return (
    <div ref={box} className={'dock' + (s.vertical ? ' vertical' : '') + (s.collapsed ? ' collapsed' : '') + (placed ? ' moved' : '')} role="toolbar" aria-label="Page tools">
      <div className="dock-ctl">
        <button className="dock-grip" aria-label="Move this bar (drag). Double-tap to put it back at the bottom." title="Drag to move · double-tap to reset" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onDoubleClick={reset}>
          ⠿
        </button>
        <button className="dock-turn" onClick={turn} aria-label={s.vertical ? 'Lay the links in a row' : 'Stack the links in a column'} title={s.vertical ? 'Row' : 'Column'}>
          ⟳
        </button>
        <button className="dock-fold" onClick={fold} aria-expanded={!s.collapsed} aria-label={s.collapsed ? 'Show the tool bar' : 'Fold the tool bar away so it does not cover the paper'} title={s.collapsed ? 'Show the tools' : 'Fold away'}>
          {s.collapsed ? '▴' : '▾'}
        </button>
      </div>
      <div className="dock-body">{children}</div>
    </div>
  );
}
